import { useState, useCallback } from "react";
import { Alert, Snackbar } from "@mui/material";

// ErrorPopup Component
const ErrorPopup = ({ open, errorMessage, onClose, severity }) => {
  const normalizeMessage = (msg) => {
    try {
      if (msg == null) return 'An error occurred.';
      if (typeof msg === 'string') return msg;
      if (Array.isArray(msg)) return msg.map(normalizeMessage).join('; ');
      if (typeof msg === 'object') {
        if (typeof msg.message === 'string') return msg.message;
        if (typeof msg.error === 'string') return msg.error;
        if (msg.error && typeof msg.error.message === 'string') return msg.error.message;
        if (Array.isArray(msg.detail)) return msg.detail.map((d) => (typeof d === 'string' ? d : (d?.msg || JSON.stringify(d)))).join('; ');
        if (typeof msg.detail === 'string') return msg.detail;
        return JSON.stringify(msg);
      }
      return String(msg);
    } catch (_) {
      return 'An error occurred.';
    }
  };

  const text = normalizeMessage(errorMessage);
  // Check if this is a detailed validation error (contains numbered list)
  const isDetailedError = typeof text === 'string' && (
    text.includes('validation error') || 
    text.includes('validation errors') ||
    text.includes('\n  1.') ||
    text.includes('\n  2.')
  );
  
  // Use longer duration for detailed errors
  const autoHideDuration = isDetailedError ? 15000 : 6000;
  
  return (
    <div>
      <Snackbar open={open} autoHideDuration={autoHideDuration} onClose={onClose}>
        <Alert 
          severity={severity} 
          onClose={onClose}
          sx={{
            '& .MuiAlert-message': {
              whiteSpace: 'pre-line', // Preserve line breaks
              fontFamily: isDetailedError ? 'monospace' : 'inherit',
              fontSize: isDetailedError ? '0.875rem' : 'inherit',
              maxWidth: isDetailedError ? '600px' : '400px',
              wordBreak: 'break-word'
            }
          }}
        >
          {text}
        </Alert>
      </Snackbar>
    </div>
  );
};

const useErrorPopup = () => {
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [severity, setSeverity] = useState("error");

  const showError = useCallback(
    (message = "An error occured.", severity = "error") => {
      // null is "nothing to report" (e.g. a cancelled delete, see formatActionError).
      if (message === null) return;
      setErrorMessage(message);
      setSeverity(severity);
      setOpen(true);
    },
    []
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const ErrorPopupComponent = (
    <ErrorPopup
      open={open}
      errorMessage={errorMessage}
      onClose={handleClose}
      severity={severity}
    />
  );

  return [showError, ErrorPopupComponent];
};

export default useErrorPopup;
